import { LoadingButton } from '@mui/lab';
import { Box, Card, Grid, Modal, TextField, Typography } from '@mui/material';
import CancelIcon from '../../icons/close.svg';

export enum NoteModalActionTypeEnum {
  Create = 'Create',
  Delete = 'Delete',
}
export interface AddDeleteNoteModalProps {
  /**
   * Flag that determines if the Modal should be opened or not
   */
  open: boolean;

  /**
   * Callback fired when the modal is closed
   */
  onClose: () => void;

  /**
   * Tells the component the purpose of this modal (Deleting or Creating a note)
   */
  actionType: NoteModalActionTypeEnum;

  /**
   * Actual text for the note
   */
  noteDescription: string;

  /**
   * Flag that tells the component that some sort of validation for note text failed
   */
  noteDescriptionValidationFailed: boolean;

  /**
   * Callback fired when the note text changes
   * @param note
   * @returns
   */
  handleNoteChange: (note: string) => void;

  /**
   * Callback fired when the user clicks the 'Add' button
   */
  handleCreateNote: () => void;

  /**
   * Callback fired when the user clicks the 'Delete' button
   */
  handleDeleteNote: () => void;

  /**
   * Flag that tells the component that the api call to delete the note is in flight
   * Used to show a local spinner on the buttons.
   */
  isDeletingNote: boolean;

  /**
   * Flag that tells the component that the api call to add a note is in flight
   * Used to show a local spinner on the buttons.
   */
  isCreatingNote: boolean;

  /**
   * Name of the author of the note
   */
  noteAuthorName: string;

  /**
   * Flag that tells the component that the api call to add a note failed.
   * Used to show an error in the modal
   */
  isCreatingNoteFailed: boolean;

  /**
   * Flag that tells the component that the api call to delete a note failed.
   * Used to show an error in the modal
   */
  isDeletingNoteFailed: boolean;
}

/**
 * This component should be used aywhere where we need the ability to add a note.
 * It should not be tightly coupled with any other component. Any implementation of the 'creation'
 * or 'deletion' of the note should be in the consumer of this component.
 * @param props
 * @returns
 */
export default function CreateDeleteNoteModal(props: AddDeleteNoteModalProps) {
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    height: '523px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    borderRadius: '24px',
    outline: 'none',
  };

  const handleClose = () => {
    props.onClose();
  };

  return (
    <Modal
      open={props.open}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      BackdropProps={{
        invisible: false,
      }}
      disableEscapeKeyDown
      disableAutoFocus={true}
    >
      <Card sx={style}>
        <Grid container display="flex" justifyContent="center">
          <Grid item xs={12} display="flex" justifyContent="center">
            <Typography
              sx={{
                fontFamily: 'Work Sans !important',
                fontStyle: 'normal',
                fontWeight: '600',
                fontSize: '18px !important',
                lineHeight: '21px !important',
                color: '#14142B',
              }}
            >
              {props.actionType === NoteModalActionTypeEnum.Create ? 'Add a Note' : props.noteAuthorName ? `${props.noteAuthorName}'s Note` : ''}
            </Typography>
          </Grid>
          {props.isCreatingNoteFailed || props.isDeletingNoteFailed ? (
            <Grid
              item
              sx={{ display: 'flex', flexDirection: 'row', borderTop: '2px  solid #DB3939', borderRadius: '24px', padding: '12px 20px', width: '360px', height: '49px', justifyContent: 'center' }}
            >
              <Typography
                sx={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: '#DB3939',
                }}
              >
                {`Apologies, something went wrong while ${props.actionType === NoteModalActionTypeEnum.Delete ? 'deleting' : 'adding'} the note. Please try again later or contact support`}
              </Typography>
            </Grid>
          ) : props.actionType === NoteModalActionTypeEnum.Delete ? (
            <Grid item mt={4} xs={12} sx={{ backgroundColor: '#F1F0F5', borderRadius: '24px', height: '380px' }} p={2}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '18px',
                  color: '#14142B',
                  marginRight: '10px',
                }}
              >
                {props.noteDescription}
              </Typography>
            </Grid>
          ) : (
            <Grid
              item
              xs={12}
              mt={4}
              sx={{
                '& fieldset': {
                  borderRadius: '24px',
                  borderColor: 'white',
                  height: '384px',
                },
              }}
            >
              <TextField
                multiline
                value={props.noteDescription}
                onChange={(e) => {
                  props.handleNoteChange(e.target.value);
                }}
                rows={8}
                sx={{ width: '100%', backgroundColor: '#F1F0F5', borderRadius: '24px', height: '380px' }}
                placeholder={'Description'}
                error={props.noteDescriptionValidationFailed}
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
          )}
          {/* Add Note Button */}
          <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                variant="contained"
                loading={props.isCreatingNote}
                style={{
                  textTransform: 'none',
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  backgroundColor: !props.noteDescription ? 'lightgrey' : '#39A2DB',
                  borderRadius: '24px',
                  width: '115px',
                  display: props.actionType === NoteModalActionTypeEnum.Create ? '' : 'none',
                }}
                onClick={props.handleCreateNote}
                disabled={!props.noteDescription}
              >
                Add
              </LoadingButton>
            </Box>
          </Box>
          {/* Delete Note Button */}
          <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <LoadingButton
                variant="outlined"
                color="error"
                loading={props.isDeletingNote}
                style={{
                  textTransform: 'none',
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  borderRadius: '24px',
                  width: '115px',
                  display: props.actionType === NoteModalActionTypeEnum.Delete ? '' : 'none',
                }}
                onClick={props.handleDeleteNote}
              >
                Delete
              </LoadingButton>
            </Box>
          </Box>
          {/* Close Icon */}
          <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
            <img
              src={CancelIcon}
              alt="close"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                handleClose();
              }}
            />
          </Grid>
        </Grid>
      </Card>
    </Modal>
  );
}
